import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { Fragment, useEffect, useState } from 'react';
import { lodash as _ } from '../../../common';
import Chip from '@material-ui/core/Chip';
import PositionChip from '../../../components/positions/PositionChip';
import { PositionsIcon } from '../../../components/icons';
import { translate } from '../../../l10n';
import { Popover } from '../../popovers';

const PositionPopoutSelector = (props) => {
   const {
      onPopoutClosed = () => {},
      onChipSelected = null,
      readOnly = false,
      showAsChip = false,
      positions,
      onChange,
      selectedPositions,
      showLabel = false,
      label = null,
      id,
      allowMultiple = false,
      required = false
   } = props;

   const labelCaption = label
      ? label
      : allowMultiple
      ? translate('positions.positionselector.positions.label')
      : translate('positions.positionselector.position.label');

   const [value, setValue] = useState([]);
   const [editing, setEditing] = useState(false);
   const [caption, setCaption] = useState('any position');
   const [placeholder, setPlaceholder] = useState(translate('positions.positionselector.placeholder.startTyping'));

   const onClick = (evt) => {
      evt.stopPropagation();
      evt.preventDefault();
   };

   const onLocalChange = (evt, newValue) => {
      let selectedPositions = allowMultiple ? newValue : newValue.length > 0 ? [newValue[newValue.length - 1]] : [];

      setValue(selectedPositions);

      if (selectedPositions.length > 0) {
         setEditing(false);
      }

      if (onChange) {
         onChange({ positions: selectedPositions });
      }
   };

   const buildPositionCaption = () => {
      let positionCaption = '';
      let names = [];

      _.each(value, (l) => {
         const ml = _.find(positions, (al) => {
            return al.no == l.no;
         });
         if (ml) {
            names.push(ml.name);
         }
      });

      const positionLimit = 3;
      const extraPositions = names.length > positionLimit ? names.length - positionLimit : 0;

      _.each(names, (n, idx) => {
         if ((idx == 1 || idx == 2) && names.length > 2) {
            positionCaption += ', ';
         }
         if ((idx == names.length - 1 || idx == positionLimit) && names.length > 1 && idx <= positionLimit) {
            positionCaption += ' and ';
         }
         if (idx < positionLimit) {
            positionCaption += n;
         }

         if (idx == positionLimit) {
            positionCaption += `${extraPositions} other`;
            if (extraPositions > 1) {
               positionCaption += 's';
            }
         }
      });

      return positionCaption;
   };

   useEffect(() => {
      let newCaption = 'any position';
      if (value && value.length > 0) {
         newCaption = buildPositionCaption().toLowerCase();
      }
      setCaption(newCaption);
      setPlaceholder(value && value.length > 0 ? (allowMultiple ? 'add more...' : '') : 'Start typing...');
   }, [value]);

   useEffect(() => {
      if (required && positions && positions.length > 0 && selectedPositions && selectedPositions.length == 0) {
         onLocalChange(null, [positions[0]]); // if none is selected, select the first one automatically
         return;
      }

      let newValue = _.filter(positions, (l) => {
         return (
            _.findIndex(selectedPositions, (sl) => {
               return sl.no == l.no;
            }) > -1
         );
      });
      newValue = _.sortBy(newValue, (l) => {
         return _.findIndex(selectedPositions, (sl) => {
            return sl.no === l.no;
         });
      });

      setValue(newValue);
   }, [selectedPositions]);

   const toggleEditing = () => {
      const newEditing = !editing;
      if (!newEditing) {
         if (onPopoutClosed) {
            onPopoutClosed();
         }
      }

      setEditing(newEditing);
   };

   const _onChipSelected = ({ evt, positions }) => {
      if (onChipSelected) {
         onChipSelected({ evt, positions });
      }
   };

   const renderPositionsAutoComplete = () => {
      return (
         <Autocomplete
            multiple
            shrink='true'
            disableClearable={required}
            filterSelectedOptions
            blurOnSelect={false}
            value={value}
            options={positions}
            onChange={onLocalChange}
            disablePortal={true}
            getOptionLabel={(option) => option.name}
            renderOption={(option, { selected }) => (
               <div data-ignore-popper-toggle style={{ width: '100%' }}>
                  {/*<Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />*/}
                  {option.name}
               </div>
            )}
            renderInput={(params) => (
               <TextField
                  {...params}
                  InputLabelProps={{
                     shrink: true
                  }}
                  variant={'standard'}
                  label={labelCaption}
                  placeholder={placeholder}
               />
            )}
            renderTags={(tagValue, getTagProps) => {
               return (
                  <Fragment>
                     {tagValue.map((option, index) => (
                        <PositionChip {...getTagProps({ index })} name={option.name} disableDelete={required} />
                     ))}
                  </Fragment>
               );
            }}
         />
      );
   };

   const popupTarget = id ? id + '-set-position-selection-spn' : 'set-position-selection-spn';

   return (
      <div className={'PositionPopoutSelector'} onClick={onClick}>
         {readOnly && !showAsChip && (
            <div>
               {showLabel && <label>{labelCaption}</label>}
               <span>{caption}</span>
            </div>
         )}
         {readOnly && showAsChip && (
            <Chip
               icon={<PositionsIcon fontSize='small' className='icon' />}
               size='small'
               color={'secondary'}
               variant='outlined'
               className={'info-chip'}
               label={caption}
               onClick={onChipSelected ? (e) => _onChipSelected({ evt: e, positions: value }) : undefined}
            />
         )}
         {!readOnly && editing && (
            <Popover placement='bottom' isOpen={editing} target={popupTarget} className='position-selector-popover' toggle={toggleEditing}>
               {renderPositionsAutoComplete()}
            </Popover>
         )}
         {!readOnly && (
            <div className='position-selector-control'>
               {showLabel && <label>{labelCaption}</label>}
               <div className='editable-caption-row'>
                  <span id={popupTarget} className='filter-link' onClick={toggleEditing}>
                     {caption}
                  </span>
               </div>
            </div>
         )}
      </div>
   );
};

export default PositionPopoutSelector;
